import {
  createMemo,
  createResource,
  createSignal,
  For,
  Show,
  type JSX,
} from "solid-js";
import { A } from "@solidjs/router";
import type { Dashboard, DashboardTile } from "@energyplatform/client";
import { api, session } from "../services.ts";
import { preferences, savePreferences } from "../preferences.ts";
import { locale, t, tOr } from "../i18n.ts";
import { formatAgo, formatDateTime, formatNumber } from "../format.ts";
import { range, scope, siteTree } from "../context.ts";
import { subtreeOf } from "../tree.ts";
import { Icon } from "../icons.tsx";
import { Chart, type ChartSeries } from "./Chart.tsx";
import { ErrorBox } from "./ErrorBox.tsx";
import { ConfirmDialog, Dialog, Skeleton, useAction } from "./ui.tsx";

export type TileSize = "s" | "m" | "l";

/** The tiles a dashboard can hold, in the order the catalogue offers them. */
export const TILE_TYPES = [
  "heading",
  "attention",
  "gridBand",
  "power",
  "energyChart",
  "balance",
  "reactive",
  "loads",
  "market",
  "pointChart",
  "pointValue",
  "events",
  "water",
  "gas",
  "air",
  "conditions",
  "map",
] as const;
export type TileType = (typeof TILE_TYPES)[number];

/** Tiles that need settings before they show anything. */
const NEEDS_SETTINGS: readonly string[] = ["pointChart", "pointValue"];
const DEFAULT_SIZE: Record<string, TileSize> = {
  heading: "l",
  attention: "l",
  gridBand: "l",
  power: "l",
  water: "l",
  gas: "l",
  air: "l",
  conditions: "l",
  map: "l",
  pointValue: "s",
};

let tileIds = 0;
export const newTileId = () =>
  `t${Date.now().toString(36)}${(tileIds++).toString(36)}`;

/** The media of the built-in tabs, in their default order. */
export const BUILTIN_GROUPS = ["el", "water", "gas", "air", "env"] as const;
export type BuiltinGroup = (typeof BUILTIN_GROUPS)[number];

/** What each built-in tab shows: one medium, with what needs attention and the sites first for electricity. */
export const BUILTIN_TILES: Record<BuiltinGroup, DashboardTile[]> = {
  el: [
    { id: "attention", type: "attention", size: "l", settings: null },
    { id: "grid", type: "gridBand", size: "l", settings: null },
    { id: "power", type: "power", size: "l", settings: null },
    { id: "energy", type: "energyChart", size: "m", settings: null },
    { id: "balance", type: "balance", size: "m", settings: null },
    { id: "loads", type: "loads", size: "m", settings: null },
    { id: "market", type: "market", size: "m", settings: null },
    { id: "reactive", type: "reactive", size: "l", settings: null },
    { id: "map", type: "map", size: "l", settings: null },
  ],
  water: [{ id: "water", type: "water", size: "l", settings: null }],
  gas: [{ id: "gas", type: "gas", size: "l", settings: null }],
  air: [{ id: "air", type: "air", size: "l", settings: null }],
  env: [{ id: "conditions", type: "conditions", size: "l", settings: null }],
};

/** A tab of the overview: a built-in medium or a dashboard. */
interface Tab {
  key: string;
  label: string;
  group?: BuiltinGroup;
  dashboard?: Dashboard;
}

const LAST_TAB = "ep.overview.tab";
const builtinKey = (g: BuiltinGroup) => `builtin:${g}`;

export interface TileSettings {
  [key: string]: unknown;
  pointId?: string;
  attribute?: string;
  text?: string;
  group?: string;
  title?: string;
}

const settingsOf = (tile: DashboardTile): TileSettings =>
  (tile.settings ?? {}) as TileSettings;

const me = () => {
  const s = session();
  return s.status === "signedIn" ? s.user : null;
};

const readLast = () => {
  try {
    return localStorage.getItem(LAST_TAB);
  } catch {
    return null;
  }
};
const writeLast = (key: string) => {
  try {
    localStorage.setItem(LAST_TAB, key);
  } catch {
    // Not remembered in this browser; the default tab opens next time.
  }
};

/**
 * The overview as tabs: one per medium of the place (electricity, water, gas, compressed air, conditions) and the
 * person's own dashboards, the organisation's default and the shared ones they added. Tabs are dragged to reorder,
 * hidden and added in "Tabs"; the last one opened opens again. A tab's tiles sit in a grid (a third, a half or the
 * whole row); in edit mode they are added, moved, resized, set up and removed. Editing a built-in tab or someone
 * else's dashboard saves one's own copy in its place. <paramref name="render"/> draws the overview's own tiles.
 */
export function DashboardView(props: {
  render: (tile: DashboardTile) => JSX.Element;
  header: (actions: JSX.Element) => JSX.Element;
  /** The media with points in the scope; the others' built-in tabs are left out. */
  groups: readonly string[];
}) {
  const [list, { refetch }] = createResource(() => api.dashboards.list());
  const arranged = () => preferences().tabs ?? { order: [], hidden: [] };
  const all = createMemo<Tab[]>(() => [
    ...BUILTIN_GROUPS.filter((g) => g === "el" || props.groups.includes(g)).map(
      (g): Tab => ({
        key: builtinKey(g),
        label: t(`overview.group.${g}`),
        group: g,
      }),
    ),
    ...(list()?.dashboards ?? []).map((d): Tab => ({
      key: d.id,
      label: d.name,
      dashboard: d,
    })),
  ]);
  // Shown by default: the built-in ones, one's own dashboards and the organisation's default; shared ones once added.
  const shownByDefault = (tab: Tab) =>
    !tab.dashboard ||
    tab.dashboard.owner === me()?.id ||
    tab.dashboard.organizationDefault;
  const tabs = createMemo<Tab[]>(() => {
    const a = arranged();
    const hidden = new Set(a.hidden);
    const named = a.order
      .map((k) => all().find((x) => x.key === k))
      .filter((x): x is Tab => x !== undefined && !hidden.has(x.key));
    const rest = all().filter(
      (x) =>
        !a.order.includes(x.key) && !hidden.has(x.key) && shownByDefault(x),
    );
    return [...named, ...rest];
  });
  const [chosen, setChosen] = createSignal<string | undefined>(
    readLast() ?? undefined,
  );
  const current = createMemo<Tab | undefined>(
    () =>
      tabs().find((x) => x.key === chosen()) ??
      tabs().find((x) => x.key === list()?.defaultId) ??
      tabs()[0],
  );
  const choose = (key: string) => {
    setChosen(key);
    writeLast(key);
  };
  const saveTabs = (order: string[], hidden: string[]) =>
    savePreferences({ tabs: { order, hidden } }).catch(() => undefined);

  const [editing, setEditing] = createSignal(false);
  const [draft, setDraft] = createSignal<DashboardTile[]>([]);
  const [name, setName] = createSignal("");
  const [shared, setShared] = createSignal(false);
  const [adding, setAdding] = createSignal(false);
  const [managing, setManaging] = createSignal(false);
  const [configuring, setConfiguring] = createSignal<DashboardTile>();
  const [removing, setRemoving] = createSignal(false);
  const [dragged, setDragged] = createSignal<string>();
  const [draggedTab, setDraggedTab] = createSignal<string>();
  const [run, error, busy] = useAction();

  const tilesOf = (tab: Tab | undefined) =>
    tab?.dashboard?.tiles ?? BUILTIN_TILES[tab?.group ?? "el"];
  const tiles = () => (editing() ? draft() : tilesOf(current()));
  const admin = () => me()?.roles.includes("admin") ?? false;
  // A built-in tab or someone else's dashboard is edited as one's own copy that takes its place.
  const ownEdit = () =>
    !!current()?.dashboard &&
    (current()!.dashboard!.owner === me()?.id || admin());

  const startEdit = () => {
    setDraft(tilesOf(current()).map((x) => ({ ...x })));
    setName(current()?.label ?? t("dashboard.overview"));
    setShared(ownEdit() ? (current()?.dashboard?.shared ?? false) : false);
    setEditing(true);
  };
  const order = () => tabs().map((x) => x.key);
  const save = () =>
    void run(async () => {
      const replaced = current();
      const saved = await api.dashboards.save(
        ownEdit() ? replaced!.dashboard!.id : null,
        {
          name: name().trim() || t("dashboard.overview"),
          shared: shared(),
          tiles: draft(),
        },
      );
      api.invalidate("/api/v1/dashboards");
      if (!ownEdit() && replaced) {
        // The copy takes the place of the tab it was made from.
        await saveTabs(
          order().map((k) => (k === replaced.key ? saved.id : k)),
          [...arranged().hidden.filter((k) => k !== saved.id), replaced.key],
        );
      }
      await refetch();
      choose(saved.id);
      setEditing(false);
    });
  const newDashboard = (copy: boolean) =>
    void run(async () => {
      const saved = await api.dashboards.save(null, {
        name: copy
          ? t("dashboard.newName", { name: current()?.label ?? "" })
          : t("dashboard.untitled"),
        shared: false,
        tiles: copy ? tilesOf(current()).map((x) => ({ ...x })) : [],
      });
      api.invalidate("/api/v1/dashboards");
      await saveTabs([...order(), saved.id], arranged().hidden);
      await refetch();
      choose(saved.id);
      setManaging(false);
      startEdit();
    });
  const dropTab = (target: string) => {
    const from = draggedTab();
    setDraggedTab(undefined);
    if (!from || from === target) {
      return;
    }
    const keys = order().filter((k) => k !== from);
    keys.splice(keys.indexOf(target), 0, from);
    void saveTabs(keys, arranged().hidden);
  };
  const move = (id: string, by: number) => {
    const next = [...draft()];
    const i = next.findIndex((x) => x.id === id);
    const j = Math.max(0, Math.min(next.length - 1, i + by));
    const [x] = next.splice(i, 1);
    next.splice(j, 0, x!);
    setDraft(next);
  };
  const dropOn = (target: string) => {
    const from = dragged();
    if (!from || from === target) {
      return;
    }
    const next = [...draft()];
    const i = next.findIndex((x) => x.id === from);
    const [x] = next.splice(i, 1);
    next.splice(
      next.findIndex((y) => y.id === target),
      0,
      x!,
    );
    setDraft(next);
    setDragged(undefined);
  };
  const update = (tile: DashboardTile) =>
    setDraft(draft().map((x) => (x.id === tile.id ? tile : x)));
  const add = (type: TileType) => {
    const tile: DashboardTile = {
      id: newTileId(),
      type,
      size: DEFAULT_SIZE[type] ?? "m",
      settings: null,
    };
    setDraft([...draft(), tile]);
    setAdding(false);
    if (NEEDS_SETTINGS.includes(type) || type === "heading") {
      setConfiguring(tile);
    }
  };

  const actions = (
    <>
      <Show when={!editing()}>
        <button type="button" class="btn" onClick={startEdit}>
          <Icon name="edit" />
          {t("dashboard.edit")}
        </button>
      </Show>
      <Show when={editing()}>
        <button type="button" class="btn" onClick={() => setEditing(false)}>
          {t("common.cancel")}
        </button>
        <button
          type="button"
          class="btn primary"
          disabled={busy()}
          onClick={save}
        >
          {t("common.save")}
        </button>
      </Show>
    </>
  );

  return (
    <>
      {props.header(actions)}
      <div class="dash-tabs" role="tablist" aria-label={t("dashboard.tabs")}>
        <For each={tabs()}>
          {(tab) => (
            <button
              type="button"
              role="tab"
              aria-selected={current()?.key === tab.key}
              class={draggedTab() === tab.key ? "dragging" : ""}
              draggable={!editing()}
              disabled={editing() && current()?.key !== tab.key}
              title={
                tab.dashboard && tab.dashboard.owner !== me()?.id
                  ? t("dashboard.by", { name: tab.dashboard.ownerName })
                  : undefined
              }
              onClick={() => choose(tab.key)}
              onDragStart={() => setDraggedTab(tab.key)}
              onDragEnd={() => setDraggedTab(undefined)}
              onDragOver={(e) => draggedTab() && e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                dropTab(tab.key);
              }}
            >
              {tab.label}
              <Show
                when={
                  tab.dashboard?.shared || tab.dashboard?.organizationDefault
                }
              >
                <span class="dash-tab-mark" aria-hidden="true">
                  ·
                </span>
              </Show>
            </button>
          )}
        </For>
        <Show when={!editing()}>
          <button
            type="button"
            class="dash-tab-add"
            aria-label={t("dashboard.manageTabs")}
            title={t("dashboard.manageTabs")}
            onClick={() => setManaging(true)}
          >
            ＋
          </button>
        </Show>
      </div>
      <Show when={editing()}>
        <section class="card">
          <div class="card-b" style={{ display: "grid", gap: "10px" }}>
            <Show when={!ownEdit()}>
              <p class="hint" style={{ margin: 0 }}>
                {t("dashboard.copyHint", { name: current()?.label ?? "" })}
              </p>
            </Show>
            <div
              class="grid g-2e"
              style={{ gap: "10px", "align-items": "end" }}
            >
              <div class="field">
                <label for="dbName">{t("dashboard.name")}</label>
                <input
                  id="dbName"
                  value={name()}
                  maxLength={80}
                  onInput={(e) => setName(e.currentTarget.value)}
                />
              </div>
              <div style={{ display: "flex", gap: "8px", "flex-wrap": "wrap" }}>
                <button
                  type="button"
                  class="btn"
                  onClick={() => setAdding(true)}
                >
                  <Icon name="plus" />
                  {t("dashboard.addTile")}
                </button>
                <Show when={ownEdit()}>
                  <button
                    type="button"
                    class="btn ghost"
                    onClick={() => setRemoving(true)}
                  >
                    <Icon name="x" />
                    {t("dashboard.delete")}
                  </button>
                </Show>
              </div>
            </div>
            <label class="opt">
              <input
                type="checkbox"
                checked={shared()}
                onChange={(e) => setShared(e.currentTarget.checked)}
              />
              <div>
                <b>{t("dashboard.share")}</b>
                <small>{t("dashboard.shareHint")}</small>
              </div>
            </label>
            <p class="hint" style={{ margin: 0 }}>
              {t("dashboard.editHint")}
            </p>
            <Show when={error()}>
              <ErrorBox error={error()} />
            </Show>
          </div>
        </section>
      </Show>
      <Show when={!editing() && current()?.dashboard}>
        {(d) => (
          <div
            class="hint"
            style={{
              display: "flex",
              gap: "10px",
              "flex-wrap": "wrap",
              "align-items": "center",
            }}
          >
            <span>
              {d().owner === me()?.id
                ? t("dashboard.yours")
                : t("dashboard.by", { name: d().ownerName })}
              {d().shared ? ` · ${t("dashboard.sharedChip")}` : ""}
              {d().organizationDefault ? ` · ${t("dashboard.orgDefault")}` : ""}
            </span>
            <Show when={admin() && !d().organizationDefault}>
              <button
                type="button"
                class="btn ghost sm"
                onClick={async () => {
                  await api.dashboards.setOrganizationDefault(d().id);
                  api.invalidate("/api/v1/dashboards");
                  void refetch();
                }}
              >
                {t("dashboard.makeOrgDefault")}
              </button>
            </Show>
          </div>
        )}
      </Show>
      <Show when={list() || list.error} fallback={<Skeleton lines={6} />}>
        <Show
          when={tiles().length > 0 || editing()}
          fallback={
            <section class="card empty">
              <p class="muted">{t("dashboard.empty")}</p>
              <div>
                <button type="button" class="btn primary" onClick={startEdit}>
                  <Icon name="plus" />
                  {t("dashboard.addTile")}
                </button>
              </div>
            </section>
          }
        >
          <div class={`dash${editing() ? " editing" : ""}`}>
            <For each={tiles()}>
              {(tile) => (
                <div
                  class={`tile tile-${tile.size}${dragged() === tile.id ? " dragging" : ""}`}
                  draggable={editing()}
                  onDragStart={() => setDragged(tile.id)}
                  onDragEnd={() => setDragged(undefined)}
                  onDragOver={(e) => editing() && e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    dropOn(tile.id);
                  }}
                >
                  <Show when={editing()}>
                    <div class="tile-bar">
                      <Icon name="grip" />
                      <b>{t(`dashboard.tile.${tile.type}`)}</b>
                      <span
                        style={{
                          "margin-left": "auto",
                          display: "flex",
                          gap: "4px",
                        }}
                      >
                        <button
                          type="button"
                          class="btn ghost sm"
                          aria-label={t("dashboard.up")}
                          onClick={() => move(tile.id, -1)}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          class="btn ghost sm"
                          aria-label={t("dashboard.down")}
                          onClick={() => move(tile.id, 1)}
                        >
                          ↓
                        </button>
                        <select
                          aria-label={t("dashboard.size")}
                          value={tile.size}
                          onChange={(e) =>
                            update({
                              ...tile,
                              size: e.currentTarget.value as TileSize,
                            })
                          }
                        >
                          <option value="s">{t("dashboard.sizes.s")}</option>
                          <option value="m">{t("dashboard.sizes.m")}</option>
                          <option value="l">{t("dashboard.sizes.l")}</option>
                        </select>
                        <Show
                          when={
                            NEEDS_SETTINGS.includes(tile.type) ||
                            tile.type === "heading"
                          }
                        >
                          <button
                            type="button"
                            class="btn ghost sm"
                            aria-label={t("dashboard.settings")}
                            onClick={() => setConfiguring(tile)}
                          >
                            <Icon name="edit" />
                          </button>
                        </Show>
                        <button
                          type="button"
                          class="btn ghost sm"
                          aria-label={t("dashboard.remove")}
                          onClick={() =>
                            setDraft(draft().filter((x) => x.id !== tile.id))
                          }
                        >
                          <Icon name="x" />
                        </button>
                      </span>
                    </div>
                  </Show>
                  {props.render(tile)}
                </div>
              )}
            </For>
          </div>
        </Show>
      </Show>
      <Show when={managing()}>
        <TabsDialog
          all={all()}
          shown={tabs().map((x) => x.key)}
          busy={busy()}
          onNew={newDashboard}
          onSave={(shown) => {
            const hidden = all()
              .map((x) => x.key)
              .filter((k) => !shown.includes(k));
            void saveTabs(shown, hidden);
            setManaging(false);
          }}
          onClose={() => setManaging(false)}
        />
      </Show>
      <Show when={adding()}>
        <Dialog
          title={t("dashboard.addTile")}
          wide
          onClose={() => setAdding(false)}
        >
          <div class="grid g-2e" style={{ gap: "8px" }}>
            <For each={TILE_TYPES}>
              {(type) => (
                <button
                  type="button"
                  class="btn"
                  style={{
                    "justify-content": "flex-start",
                    "text-align": "left",
                  }}
                  onClick={() => add(type)}
                >
                  <div>
                    <b>{t(`dashboard.tile.${type}`)}</b>
                    <div class="hint">{t(`dashboard.tileHint.${type}`)}</div>
                  </div>
                </button>
              )}
            </For>
          </div>
        </Dialog>
      </Show>
      <Show when={configuring()}>
        {(tile) => (
          <TileSettingsDialog
            tile={tile()}
            onClose={() => setConfiguring(undefined)}
            onSave={(settings) => {
              update({ ...tile(), settings });
              setConfiguring(undefined);
            }}
          />
        )}
      </Show>
      <Show when={removing() && current()?.dashboard}>
        <ConfirmDialog
          title={t("dashboard.deleteTitle", { name: current()!.label })}
          text={t("dashboard.deleteText")}
          confirm={t("dashboard.delete")}
          danger
          onConfirm={async () => {
            const id = current()!.dashboard!.id;
            await api.dashboards.remove(id);
            api.invalidate("/api/v1/dashboards");
            setEditing(false);
            await saveTabs(
              order().filter((k) => k !== id),
              arranged().hidden.filter((k) => k !== id),
            );
            setChosen(undefined);
            await refetch();
          }}
          onClose={() => setRemoving(false)}
        />
      </Show>
    </>
  );
}

/**
 * The tabs a person keeps: every built-in tab and dashboard they may open, ticked when shown, in their order (arrows),
 * and new ones — empty or a copy of the tab open now.
 */
function TabsDialog(props: {
  all: Tab[];
  shown: string[];
  busy: boolean;
  onNew: (copy: boolean) => void;
  onSave: (shown: string[]) => void;
  onClose: () => void;
}) {
  const [keys, setKeys] = createSignal<string[]>([
    ...props.shown,
    ...props.all.map((x) => x.key).filter((k) => !props.shown.includes(k)),
  ]);
  const [on, setOn] = createSignal(new Set(props.shown));
  const tab = (k: string) => props.all.find((x) => x.key === k)!;
  const move = (i: number, by: number) => {
    const next = [...keys()];
    const j = Math.max(0, Math.min(next.length - 1, i + by));
    const [x] = next.splice(i, 1);
    next.splice(j, 0, x!);
    setKeys(next);
  };
  return (
    <Dialog
      title={t("dashboard.manageTabs")}
      onClose={props.onClose}
      footer={
        <>
          <button type="button" class="btn" onClick={() => props.onClose()}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            class="btn primary"
            onClick={() => props.onSave(keys().filter((k) => on().has(k)))}
          >
            {t("common.save")}
          </button>
        </>
      }
    >
      <div style={{ display: "grid", gap: "12px" }}>
        <div style={{ display: "flex", gap: "8px", "flex-wrap": "wrap" }}>
          <button
            type="button"
            class="btn"
            disabled={props.busy}
            onClick={() => props.onNew(false)}
          >
            <Icon name="plus" />
            {t("dashboard.newEmpty")}
          </button>
          <button
            type="button"
            class="btn"
            disabled={props.busy}
            onClick={() => props.onNew(true)}
          >
            {t("dashboard.newCopy")}
          </button>
        </div>
        <div>
          <For each={keys()}>
            {(k, i) => (
              <div class="wz-block">
                <input
                  type="checkbox"
                  checked={on().has(k)}
                  aria-label={tab(k).label}
                  onChange={(e) => {
                    const next = new Set(on());
                    if (e.currentTarget.checked) {
                      next.add(k);
                    } else {
                      next.delete(k);
                    }
                    setOn(next);
                  }}
                />
                <span class="nm">{tab(k).label}</span>
                <span class="hint">
                  {tab(k).group
                    ? t("dashboard.builtin")
                    : tab(k).dashboard!.owner === me()?.id
                      ? t("dashboard.yours")
                      : t("dashboard.by", {
                          name: tab(k).dashboard!.ownerName,
                        })}
                </span>
                <span
                  style={{ "margin-left": "auto", display: "flex", gap: "4px" }}
                >
                  <button
                    type="button"
                    class="btn ghost sm"
                    aria-label={t("dashboard.up")}
                    onClick={() => move(i(), -1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    class="btn ghost sm"
                    aria-label={t("dashboard.down")}
                    onClick={() => move(i(), 1)}
                  >
                    ↓
                  </button>
                </span>
              </div>
            )}
          </For>
        </div>
        <p class="hint" style={{ margin: 0 }}>
          {t("dashboard.tabsHint")}
        </p>
      </div>
    </Dialog>
  );
}

/** Settings of a tile: the point (and quantity) of a chart or value, the text of a heading. */
function TileSettingsDialog(props: {
  tile: DashboardTile;
  onClose: () => void;
  onSave: (settings: TileSettings) => void;
}) {
  const s = settingsOf(props.tile);
  const points = () =>
    (siteTree()?.points ?? [])
      .filter((p) => p.binding)
      .sort((a, b) => a.name.localeCompare(b.name));
  const [pointId, setPointId] = createSignal(
    s.pointId ?? points()[0]?.id ?? "",
  );
  const [attribute, setAttribute] = createSignal(s.attribute ?? "");
  const [text, setText] = createSignal(s.text ?? "");
  const [title, setTitle] = createSignal(s.title ?? "");
  const point = () => points().find((p) => p.id === pointId());
  const [quantities] = createResource(
    () =>
      props.tile.type === "pointChart" ? point()?.binding?.deviceId : undefined,
    async (id) => [
      ...new Set(
        (await api.devices.latest(id))
          .filter((v) => v.number !== null)
          .map((v) => v.attribute),
      ),
    ],
  );
  return (
    <Dialog
      title={t(`dashboard.tile.${props.tile.type}`)}
      onClose={props.onClose}
      footer={
        <>
          <button type="button" class="btn" onClick={() => props.onClose()}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            class="btn primary"
            onClick={() =>
              props.onSave(
                props.tile.type === "heading"
                  ? {
                      text: text().trim() || undefined,
                      group: text().trim() ? undefined : s.group,
                    }
                  : {
                      pointId: pointId(),
                      attribute: attribute() || undefined,
                      title: title().trim() || undefined,
                    },
              )
            }
          >
            {t("common.save")}
          </button>
        </>
      }
    >
      <div style={{ display: "grid", gap: "10px" }}>
        <Show
          when={props.tile.type !== "heading"}
          fallback={
            <div class="field">
              <label for="tsText">{t("dashboard.headingText")}</label>
              <input
                id="tsText"
                value={text()}
                maxLength={80}
                onInput={(e) => setText(e.currentTarget.value)}
              />
            </div>
          }
        >
          <div class="field">
            <label for="tsPoint">{t("dashboard.point")}</label>
            <select
              id="tsPoint"
              value={pointId()}
              onChange={(e) => setPointId(e.currentTarget.value)}
            >
              <For each={points()}>
                {(p) => <option value={p.id}>{p.name}</option>}
              </For>
            </select>
          </div>
          <Show
            when={
              props.tile.type === "pointChart" &&
              (quantities() ?? []).length > 0
            }
          >
            <div class="field">
              <label for="tsAttr">{t("dashboard.quantity")}</label>
              <select
                id="tsAttr"
                value={attribute()}
                onChange={(e) => setAttribute(e.currentTarget.value)}
              >
                <option value="">{t("dashboard.pointValue")}</option>
                <For each={quantities()}>
                  {(q) => <option value={q}>{tOr(`attribute.${q}`, q)}</option>}
                </For>
              </select>
              <span class="hint">{t("dashboard.quantityHint")}</span>
            </div>
          </Show>
          <div class="field">
            <label for="tsTitle">{t("dashboard.title")}</label>
            <input
              id="tsTitle"
              value={title()}
              maxLength={80}
              onInput={(e) => setTitle(e.currentTarget.value)}
            />
          </div>
        </Show>
      </div>
    </Dialog>
  );
}

const pointOf = (id: string | undefined) =>
  siteTree()?.points.find((p) => p.id === id);

/** Tile: a point's value over the period, or one quantity of its meter in every channel (e.g. phase voltages). */
export function PointChartTile(props: { tile: DashboardTile }) {
  const s = () => settingsOf(props.tile);
  const point = () => pointOf(s().pointId);
  const [series] = createResource(
    () => {
      const p = point();
      return p?.binding ? { p, attr: s().attribute, ...range() } : undefined;
    },
    async (q) => {
      const latest = await api.devices.latest(q.p.binding!.deviceId);
      const attr =
        q.attr ??
        latest.find(
          (v) => v.channel === q.p.binding!.channel && v.number !== null,
        )?.attribute ??
        "power.active";
      const channels = q.attr
        ? [
            ...new Set(
              latest.filter((v) => v.attribute === attr).map((v) => v.channel),
            ),
          ]
        : [q.p.binding!.channel];
      const unit = latest.find((v) => v.attribute === attr)?.unit ?? "";
      const all = await Promise.all(
        channels.map(async (c) => ({
          c,
          points: await api.devices.series(
            q.p.binding!.deviceId,
            c,
            attr,
            q.from,
            q.to,
            "15m",
          ),
        })),
      );
      return { attr, unit, all };
    },
  );
  const chart = createMemo<ChartSeries[]>(() =>
    (series()?.all ?? []).map((x, i) => ({
      key: x.c,
      label: tOr(`channel.${x.c}`, x.c),
      color: `var(--s${(i % 6) + 1})`,
      kind: "line" as const,
      points: x.points.map((p) => ({
        t: new Date(p.at).getTime() + 450_000,
        v: p.value,
      })),
    })),
  );
  return (
    <section class="card">
      <div class="card-h">
        <h2>{s().title ?? point()?.name ?? t("dashboard.tile.pointChart")}</h2>
        <span class="meta">
          {series()
            ? `${tOr(`attribute.${series()!.attr}`, series()!.attr)} · ${series()!.unit}`
            : ""}
        </span>
      </div>
      <div class="card-b">
        <Show
          when={point()}
          fallback={<p class="hint">{t("dashboard.pickPoint")}</p>}
        >
          <Show
            when={!series.error}
            fallback={<ErrorBox error={series.error} />}
          >
            <Show when={series()} fallback={<Skeleton lines={4} />}>
              <Chart
                series={chart()}
                from={range().from}
                to={range().to}
                unit={series()!.unit === "1" ? "" : series()!.unit}
                locale={locale()}
                height={200}
                tight={[
                  "voltage",
                  "frequency",
                  "power.factor",
                  "temperature",
                  "humidity",
                  "pressure",
                ].includes(series()!.attr)}
                emptyText={t("analysis.par.noData")}
              />
            </Show>
          </Show>
        </Show>
      </div>
    </section>
  );
}

/** Tile: one point's value now, large, with how fresh it is. */
export function PointValueTile(props: { tile: DashboardTile }) {
  const s = () => settingsOf(props.tile);
  const point = () => pointOf(s().pointId);
  const [reading] = createResource(
    () => point()?.nodeId,
    async (nodeId) =>
      (await api.sites.pointsNow(nodeId)).find(
        (r) => r.pointId === s().pointId,
      ) ?? null,
  );
  return (
    <section class="card kpi">
      <span class="lbl">
        {s().title ?? point()?.name ?? t("dashboard.tile.pointValue")}
      </span>
      <Show
        when={point()}
        fallback={<span class="q">{t("dashboard.pickPoint")}</span>}
      >
        <span class="val">
          {reading()?.value == null
            ? "—"
            : formatNumber(reading()!.value!, locale(), 2)}
          <small>
            {reading()?.unit === "1" ? "" : (reading()?.unit ?? "")}
          </small>
        </span>
        <span class="q">
          {reading()?.at ? formatAgo(reading()!.at!, locale()) : ""}
          {reading()?.attribute
            ? ` · ${tOr(`attribute.${reading()!.attribute}`, reading()!.attribute!)}`
            : ""}
        </span>
      </Show>
    </section>
  );
}

/** Tile: events that are on now in the scope, newest first. */
export function EventsTile() {
  const [events] = createResource(() => api.events.list());
  const open = createMemo(() => {
    const tree = siteTree();
    const id = scope()?.id;
    const nodes = new Set(tree && id ? subtreeOf(tree.nodes, id) : []);
    const points = new Set(
      (tree?.points ?? []).filter((p) => nodes.has(p.nodeId)).map((p) => p.id),
    );
    return (events() ?? [])
      .filter(
        (e) =>
          e.endedAt === null &&
          !e.test &&
          (nodes.size === 0 ||
            (e.nodeId && nodes.has(e.nodeId)) ||
            points.has(e.subjectId)),
      )
      .slice(0, 8);
  });
  return (
    <section class="card">
      <div class="card-h">
        <h2>{t("dashboard.tile.events")}</h2>
        <A class="meta" href="/events">
          {t("dashboard.allEvents")}
        </A>
      </div>
      <Show when={events()} fallback={<Skeleton lines={3} />}>
        <Show
          when={open().length > 0}
          fallback={<div class="card-b hint">{t("dashboard.noEvents")}</div>}
        >
          <table class="tbl">
            <tbody>
              <For each={open()}>
                {(e) => (
                  <tr>
                    <td>
                      <A href={`/events/${encodeURIComponent(e.id)}`}>
                        {t(`events.kind.${e.kind}`)}
                      </A>
                      <div class="hint">{e.subjectName}</div>
                    </td>
                    <td class="hint" style={{ "white-space": "nowrap" }}>
                      {formatDateTime(e.startedAt, locale())}
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </Show>
      </Show>
    </section>
  );
}

/** Tile: a heading — a medium's section head or a text of one's own. */
export function HeadingTile(props: {
  tile: DashboardTile;
  group: (g: string) => JSX.Element;
}) {
  const s = () => settingsOf(props.tile);
  return (
    <Show
      when={s().text}
      fallback={
        s().group ? (
          props.group(s().group!)
        ) : (
          <div class="sect-h">
            <h2>{t("dashboard.tile.heading")}</h2>
          </div>
        )
      }
    >
      <div class="sect-h">
        <h2>{s().text}</h2>
      </div>
    </Show>
  );
}
